import { useRef, useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import getNowDate from '@sdk/utils/getNowDate';
import useToast from '@sdk/common/toast/useToast';
import { usePersonaCollection } from '@sdk/personaCollection/PersonaCollectionContext';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';

import { FormSubmitStatuses } from '@web/forms/types';
import {
  ValidationResults,
  UseCreateTimesheetModalProps,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import { useCreateTimesheetModalForm } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.form';
import { validateCreateTimesheetForm } from '@web/timesheets/CreateTimesheetModal/utils/validation';

export const useCreateTimesheetModal = ({
  close,
  onCreateTimesheet,
}: UseCreateTimesheetModalProps) => {
  const modalConfig = { hideClose: true, noDismiss: true };
  const today = useRef(getNowDate()).current;
  const { formData, formValues, locationOptions, positionOptions, submit } =
    useCreateTimesheetModalForm({
      initialDate: today,
    });
  const { fields, dynamicLists, dirty: isFormDataDirty } = formData;
  const { users } = usePersonaCollection();
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<ValidationResults>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const { showErrorToast } = useToast();

  useEffect(() => {
    const validationResults = validateCreateTimesheetForm(formValues);
    const isFormValid = !Object.keys(validationResults).length;
    setCanSubmit(isFormValid);

    if (isFormDataDirty && submitAttempted && !isEqual(submitErrors, validationResults)) {
      setSubmitErrors(validationResults);
    }
  }, [formValues, submitErrors, submitAttempted, isFormDataDirty]);

  const addEntry = () => {
    dynamicLists.timeClock.addItem();
  };

  const onEmployeeChange = (employees: PersonaPickerOption[]) => {
    if (employees.length) {
      const [employee] = employees;
      const user = users.find(({ id }) => id === employee.id);

      const userIsAssignedToOnlyOneLocation = user?.locationIds.length === 1;
      if (userIsAssignedToOnlyOneLocation) {
        const [locationId] = user.locationIds;
        const locationOption = locationOptions.find(({ id }) => id === locationId);
        if (locationOption) {
          fields.location.onChange([locationOption]);
        }
      }

      const userIsAssignedToOnlyOnePosition = user?.positionIds.length === 1;
      if (userIsAssignedToOnlyOnePosition) {
        const [positionId] = user.positionIds;
        const positionOption = positionOptions.find(({ id }) => id === positionId);
        if (positionOption) {
          fields.position.onChange([positionOption]);
        }
      }
    }
    fields.employee.onChange(employees);
  };

  const onModalClose = () => {
    formData.reset();
    setSubmitErrors(null);
    setSubmitAttempted(false);
    close();
  };

  const onSubmit = async () => {
    try {
      setSubmitAttempted(true);
      await submit();
      onModalClose();
      onCreateTimesheet();
    } catch (e) {
      if (
        e.status === FormSubmitStatuses.fail &&
        e.errors?.formErrors &&
        Object.keys(e.errors.formErrors).length
      ) {
        setSubmitErrors(e.errors.formErrors);
      } else {
        showErrorToast(e.errors);
      }

      // Trigger X animation on progress button
      throw e;
    }
  };

  return {
    modalConfig,
    onModalClose,
    fields,
    onEmployeeChange,
    locationOptions,
    positionOptions,
    dynamicLists,
    addEntry,
    canSubmit,
    onSubmit,
    submitErrors,
    setSubmitErrors,
  };
};
